import { useState } from "react";
import { Button } from "../../components/button/button";
import { AiService, PartialMskSummaryResponse } from "../../utilities/services/ai-service";
import UserLatestMskScoreService from "../../utilities/services/user-latest-msk-score-service";
import { buildPartialMskScore } from "../../utilities/msk-mapping";
import { MskScore } from "../../models/interfaces/scores/msk-score";
import { createLogger } from "../../utilities/logger";

const log = createLogger("PartialMskSummaryTool");

const COMPONENT_CLASS = "c-partial-msk-summary-tool";

const PartialMskSummaryTool: React.FC = () => {
    const [userId, setUserId] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [result, setResult] = useState<string>("");
    const [partialMskSent, setPartialMskSent] = useState<string>("");

    const handleSubmit = async () => {
        if (!userId || userId.trim().length === 0) {
            return;
        }

        setLoading(true);
        setResult("");
        setPartialMskSent("");

        try {
            log.debug("[Partial MSK Summary Tool] Fetching MSK score for userId", { userRecordId: userId.trim() });

            const mskScoreResults = await UserLatestMskScoreService.getBy(
                [{ field: "userId", operator: "==", value: userId.trim() }],
                [],
                1
            );

            const mskScore = mskScoreResults?.[0] as MskScore | undefined;

            if (!mskScore) {
                throw new Error(`No MSK score found for user: ${userId}`);
            }

            log.debug("[Partial MSK Summary Tool] Full MSK Score retrieved", { mskScore });
            log.debug("[Partial MSK Summary Tool] Full MSK Score ID", { id: mskScore.id });

            log.debug("[Partial MSK Summary Tool] Building partial MSK score...");
            const partialMskScore = buildPartialMskScore(mskScore);

            log.debug("[Partial MSK Summary Tool] Partial MSK Score created", { partialMskScore });

            const partialMskString = JSON.stringify(partialMskScore, null, 2);
            setPartialMskSent(partialMskString);

            const response: PartialMskSummaryResponse = await AiService.partialMskSummary(partialMskScore);
            log.debug("[Partial MSK Summary Tool] AI response", { response });
            setResult(JSON.stringify(response, null, 2));
        } catch (error: unknown) {
            log.error("[Partial MSK Summary Tool] Error", {
                exception: error,
            });
            const errorMessage = error instanceof Error ? error.message : "Failed to get partial MSK summary";
            setResult(`Error: ${errorMessage}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={COMPONENT_CLASS}>
            <h2>AI Partial MSK Summary</h2>
            <p>
                This tool retrieves the latest MSK score for a user and sends a
                partial subset of the MSK score object to the AI for summarization.
            </p>

            <div className={`${COMPONENT_CLASS}__input`}>
                <label>User ID</label>
                <input
                    type="text"
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                    placeholder="Enter user ID"
                />
            </div>

            <div className={`${COMPONENT_CLASS}__actions`}>
                <Button
                    onClick={handleSubmit}
                    buttonText={loading ? "Loading..." : "Get Partial MSK Summary"}
                    disabled={loading || !userId || userId.trim().length === 0}
                />
            </div>

            <div className={`${COMPONENT_CLASS}__content`}>
                {partialMskSent && (
                    <div className={`${COMPONENT_CLASS}__sent`}>
                        <label>Partial MSK Sent to AI</label>
                        <pre>{partialMskSent}</pre>
                    </div>
                )}

                {result && (
                    <div className={`${COMPONENT_CLASS}__result`}>
                        <label>AI Response</label>
                        <pre>{result}</pre>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PartialMskSummaryTool;